import { ConfigurationProps } from '../Configuration';
import {
	wrapChangeEventNumber,
	render_length,
	render_weight,
	render_length_unit,
} from '../shared/shared';
import ContentItem, { ButtonProps } from './ContentItem';
import {
	Pallet_t,
	Box_t,
	PalletConfiguration,
	Layer_t,
	SlipSheet_t,
	PalletConfigObj,
} from 'blackbox';
import { UnitsSystem } from 'libapi';
import React, { useState, ReactElement, Fragment, useMemo, useContext } from 'react';
import { getSlipSheetHeight } from './Stacks/stackUtils';
import StackScene from '../three/StackScene';
import {VisualizerProps} from '../three/utils';
import { ConfigurationsContext } from '../../App';

interface SummaryStats {
	totalBoxes: number;
	totalSkus: number;
	totalLayers: number;
	totalHeight: number;
	totalWeight: number;
	palletName: ReactElement;
}

function getPalletName(
	pPallet: Pallet_t,
	pUnitsSystem: UnitsSystem
): ReactElement {
	const { width, length } = pPallet.Dimensions;
	return (
		<Fragment>
			<h4> {pPallet.name} </h4>
			<h4>
				{`(${render_length(width, pUnitsSystem)} x ${render_length(
					length,
					pUnitsSystem
				)} ${render_length_unit(pUnitsSystem)})`}
			</h4>
		</Fragment>
	);
}

function getStats(
	pAllPallets: Pallet_t[],
	pAllBoxes: Box_t[],
	pAllSlipSheets: SlipSheet_t[],
	pPalletIndex: number,
	pStackIndex: number,
	pUnitsSystem: UnitsSystem
): SummaryStats {
	let lSummaryStats: SummaryStats = {
			totalBoxes: 0,
			totalLayers: 0,
			totalHeight: pAllPallets[pPalletIndex].Dimensions.height,
			totalWeight: 0,
			totalSkus: 0,
			palletName: getPalletName(pAllPallets[pPalletIndex], pUnitsSystem),
		},
		lPallet = pAllPallets[pPalletIndex],
		lStack = lPallet.Stack[pStackIndex],
		lAllSkus: number[] = [];
	lSummaryStats.totalLayers = lStack.layers.length;
	lStack.layers.forEach(function (pLayout: Layer_t, pIdx: number) {
		const lLayout = lPallet.Layouts[pLayout.index];
		if (undefined === lLayout) return;
		let lBoxWeight = 0;
		let lBoxHeight = 0;
		if (lLayout.boxPositions.length > 0) {
			const lBoxIndex = lLayout.boxPositions[0].index;
			const lFirstBox = pAllBoxes[lBoxIndex];
			lBoxWeight = lFirstBox.weight || 0;
			lBoxHeight = lFirstBox.dimensions.height;
			if (lAllSkus.indexOf(lBoxIndex) < 0) lAllSkus.push(lBoxIndex);
		}
		let lBoxCnt = 0;
		for (const bPos of lLayout.boxPositions) {
			lBoxCnt += (bPos.multipick || 0) + 1;
		}
		lSummaryStats.totalHeight += lBoxHeight;
		lSummaryStats.totalHeight += getSlipSheetHeight(
			pIdx,
			lStack.layers.length,
			pAllSlipSheets,
			lStack.slipSheet
		);
		lSummaryStats.totalBoxes += lBoxCnt;
		lSummaryStats.totalWeight += lBoxCnt * lBoxWeight;
	});
	lSummaryStats.totalHeight += getSlipSheetHeight(
		lStack.layers.length,
		lStack.layers.length,
		pAllSlipSheets,
		lStack.slipSheet
	);
	lSummaryStats.totalSkus = lAllSkus.length;
	return lSummaryStats;
}

export default function ConfigurationSummary({
	allPallets,
	allBoxes,
	allSlipSheets,
	handleNext,
	instructionNumber,
	unitsSystem,
	triggerSelect,
}: ConfigurationProps) {
	const { all_configs: lConfigs } = useContext(ConfigurationsContext) as any;
	const lPalletConfig = lConfigs as PalletConfigObj;
	const [lAbsoluteStackIndex, lSetAbsoluteStackIndex] = useState<number>(0),
		lAllStacks = useMemo(
			function () {
				return allPallets
					.map(function (pPallet, pPalletIndex) {
						return pPallet.Stack.map(function (pStack, pStackIndex) {
							return [pPalletIndex, pStackIndex] as [number, number];
						});
					})
					.flat();
			},
			[allPallets]
		),
		[lPalletIndex, lStackIndex] = lAllStacks[lAbsoluteStackIndex],
		LeftButton: ButtonProps = {
			name: '',
			action: () => {
				return;
			},
		},
		RightButton: ButtonProps = {
			name: '',
			action: handleNext,
			enabled: false,
		};
	const lTriggerStackSelect = function (pEvent: React.SyntheticEvent) {
		if (!triggerSelect) return;
		pEvent.preventDefault();
		triggerSelect(
			'Select Stack',
			lAllStacks.map(
				pStackPair => allPallets[pStackPair[0]].Stack[pStackPair[1]].name
			),
			lSetAbsoluteStackIndex,
			lAbsoluteStackIndex
		);
	};

	let instruction = 'Configuration Summary',
		contentItemProps = {
			instruction,
			instructionNumber,
			LeftButton,
			RightButton,
			sub_step: 1,
			sub_step_count: 3,
		},
		cells: SummaryCellProps[] = [];
	const lSummaryStats = getStats(
		allPallets,
		allBoxes,
		allSlipSheets,
		lPalletIndex,
		lStackIndex,
		unitsSystem
	);
	cells.push({
		title: 'Pallet Name',
		detail: lSummaryStats.palletName,
	});
	cells.push({
		title: 'Total Boxes',
		detail: <h4> {String(lSummaryStats.totalBoxes)} </h4>,
	});
	cells.push({
		title: 'Total SKUs',
		detail: <h4> {String(lSummaryStats.totalSkus)}</h4>,
	});
	cells.push({
		title: 'Total Layers',
		detail: <h4> {String(lSummaryStats.totalLayers)} </h4>,
	});
	cells.push({
		title: 'Total Height',
		detail: (
			<h4>{render_length(lSummaryStats.totalHeight, unitsSystem, true, 2)}</h4>
		),
	});
	cells.push({
		title: 'Total Weight',
		/* action: () => jumpToStep(ConfigStep.Stacks), */
		detail: (
			<h4>{render_weight(lSummaryStats.totalWeight, unitsSystem, true, 2)}</h4>
		),
	});
	const viz_props: VisualizerProps = {
		palletConfig: {
			name: '',
			boxes: allBoxes,
			slipsheets: allSlipSheets,
			pallets: allPallets,
		} as PalletConfiguration,
		pConfig: {
			...lPalletConfig,
			config: {
				...lPalletConfig.config,
				boxes: allBoxes,
				slipsheets: allSlipSheets,
				pallets: allPallets,
			}
		} as PalletConfigObj,
		pallet_index: lPalletIndex,
		stack_index: lStackIndex,
	};
	return (
		<div className="full-size grid-col-50">
			<div className="full-size flex-row align-center flex-between">
				<section className="DisplayContainer">
					<header className="full-width height-auto double-padding-top flex-center">
						<select
							className="width-60"
							value={lAbsoluteStackIndex}
							onChange={wrapChangeEventNumber(lSetAbsoluteStackIndex)}
							onMouseDown={lTriggerStackSelect}
						>
							{lAllStacks.map(function (pStackPair, pAbsoluteIndex) {
								const lStackName = allPallets[pStackPair[0]].Stack[pStackPair[1]].name;
								return (
									<option key={pAbsoluteIndex} value={pAbsoluteIndex}>
										{lStackName}
									</option>
								);
							})}
						</select>
					</header>
					{lAllStacks.length > 0 && viz_props.palletConfig && <StackScene palletConfig={viz_props.pConfig} stackIndex={viz_props.stack_index} palletIndex={viz_props.pallet_index} bayIndex={-1} useHighlights={false} zoom={1.8} autoRotate={true}/>}
				</section>
			</div>
			<div className="full-size flex-center align-center">
				<section className="StatContainer">
					{cells.map((c: SummaryCellProps, i: number) => {
						return <SummaryCell {...c} key={i} />;
					})}
				</section>
			</div>
		</div>
	);
}

interface SummaryCellProps {
	title: string;
	detail: ReactElement;
}

function SummaryCell({ title, detail }: SummaryCellProps) {
	return (
		<div className="full-size rounded flex-center align-center">
			<div className="full-width-minus-pad full-height-minus-pad rounded flex-center flex-col align-center dark-border background-desert-storm">
				<span className="font-5 color-blue bold-font margin-bottom">{title}</span>
				<span className="bold-font font-7 color-blue">{detail}</span>
			</div>
		</div>
	);
}
